import type {
  ReassignmentDetailsResponse,
  ReassignmentPrefillResponse,
  ReassignmentReasonResponse,
} from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchReassignmentApi } from "../../../../../api/client";
import { reassignmentKeys } from "./reassignmentKeys";

/**
 * Saved reassignment record for the deal (BFF `GET .../reassignment`). Gated on `enabled`
 * so it only fetches while the popup is open; `staleTime: 0` so reopening reflects the latest save.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {boolean} enabled - Whether the query should run (e.g. the popup is open).
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function reassignmentQuery(approvalId: string, enabled: boolean) {
  return queryOptions({
    queryKey: reassignmentKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<ReassignmentDetailsResponse> => {
      const response = await workbenchReassignmentApi.getReassignment(approvalId, { signal });
      // No saved record is a 200 with an empty body (axios yields ""), so normalize to an empty record.
      return response.data || {};
    },
    enabled: enabled && !!approvalId,
    staleTime: 0,
  });
}

/**
 * Reads the saved reassignment record. Disabled until `approvalId` is present and `enabled` is true.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {boolean} enabled - Whether the query should run.
 * @returns {ReturnType<typeof useQuery<ReassignmentDetailsResponse>>} The TanStack Query result.
 */
export function useReassignment(approvalId: string, enabled: boolean) {
  return useQuery(reassignmentQuery(approvalId, enabled));
}

/**
 * Reads the read-only AutoPay flag from the BFF prefill endpoint. Only `autopayAuthFlag` is used:
 * the phone/fax defaults apply to the exit flow's insert mode, not this popup.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {boolean} enabled - Whether the query should run.
 * @returns {ReturnType<typeof useQuery<ReassignmentPrefillResponse>>} The TanStack Query result.
 */
export function useReassignmentPrefill(approvalId: string, enabled: boolean) {
  return useQuery({
    queryKey: reassignmentKeys.prefill({ approvalId }),
    queryFn: async ({ signal }): Promise<ReassignmentPrefillResponse> => {
      const response = await workbenchReassignmentApi.getReassignmentPrefill(approvalId, { signal });
      return response.data;
    },
    enabled: enabled && !!approvalId,
    staleTime: 0,
  });
}

/**
 * Active reassignment reasons (`{ id, code, name }`) in display order.
 * @param {boolean} enabled - Whether the query should run.
 * @returns {ReturnType<typeof useQuery<ReassignmentReasonResponse[]>>} The TanStack Query result.
 */
export function useReassignmentReasons(enabled: boolean) {
  return useQuery({
    queryKey: reassignmentKeys.reasons(),
    queryFn: async ({ signal }): Promise<ReassignmentReasonResponse[]> => {
      const response = await workbenchReassignmentApi.listReassignmentReasons({ signal });
      return response.data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}
